// src/components/forms/shared/AddressSection.tsx
// Componente reutilizable para direcciones (facturación y envío)
// Usado por: OrderForm y CotizacionForm

import React from 'react'
import type { Departamento, Ciudad } from '../../../types/orderFormTypes'
import type { BodegaRecogidaSeleccion } from '../../../hooks/order/useRecogeEnBodega'
import { Input, Select, Switch } from '../../ui'
import { FormField } from '../../ui/Form'
import SectionTitle from '../../ui/SectionTitle'
import { MapPinIcon, TruckIcon } from '@heroicons/react/24/outline'

interface AddressSectionProps {
  // Datos de direcciones
  billingAddress: {
    address_1?: string
    address_2?: string
    city?: string
    state?: string
    phone?: string
  }
  shippingAddress: {
    address_1?: string
    address_2?: string
    city?: string
    state?: string
    phone?: string
  }
  
  // Handlers
  onBillingAddressChange: (field: string, value: string) => void
  onShippingAddressChange: (field: string, value: string) => void
  
  // Configuración
  mode: 'view' | 'edit' | 'create'
  departamentos: Departamento[]
  ciudades: Ciudad[]
  errors: { [key: string]: string }
  
  // Funciones de renderizado
  renderDepartamentoOptions: (addressType: 'billing' | 'shipping') => React.ReactNode
  renderCiudadOptions: (addressType: 'billing' | 'shipping') => React.ReactNode
  
  // Funcionalidad específica de OrderForm
  recogeEnBodega?: boolean
  onRecogeEnBodegaChange?: (value: boolean) => void
  showRecogeEnBodega?: boolean
  bodegaRecogidaSeleccion?: BodegaRecogidaSeleccion
  onBodegaRecogidaSeleccionChange?: (value: BodegaRecogidaSeleccion) => void
  bodegaRecogidaError?: string
  
  // Ocultar dirección 2 (para CotizacionForm que no usa address_2)
  showAddress2?: boolean
}

export const AddressSection: React.FC<AddressSectionProps> = ({
  billingAddress,
  shippingAddress,
  onBillingAddressChange,
  onShippingAddressChange,
  mode,
  departamentos,
  ciudades,
  errors,
  renderDepartamentoOptions,
  renderCiudadOptions,
  recogeEnBodega = false,
  onRecogeEnBodegaChange,
  showRecogeEnBodega = false,
  bodegaRecogidaSeleccion = '',
  onBodegaRecogidaSeleccionChange,
  bodegaRecogidaError,
  showAddress2 = true
}) => {
  const isReadOnly = mode === 'view' || recogeEnBodega
  
  // Convertir opciones renderizadas a formato Select
  const getSelectOptions = (renderFunction: (addressType: 'billing' | 'shipping') => React.ReactNode, addressType: 'billing' | 'shipping'): Array<{ value: string | number; label: string; disabled?: boolean }> => {
    try {
      const rendered = renderFunction(addressType)
      if (Array.isArray(rendered)) {
        const options: Array<{ value: string | number; label: string; disabled?: boolean }> = []
        
        rendered.forEach((option: any) => {
          // Manejar elementos React
          if (option && typeof option === 'object') {
            // Si es un elemento React con props
            if (option.props) {
              const value = option.props.value || ''
              const children = option.props.children
              // Si children es un string, usarlo como label
              const label = typeof children === 'string' 
                ? children 
                : (Array.isArray(children) ? children.join('') : value || '')
              
              if (value !== undefined) {
                options.push({
                  value,
                  label: label || value,
                  disabled: option.props.disabled || false
                })
              }
            } else if (option.value !== undefined) {
              // Si es un objeto plano
              options.push({
                value: option.value,
                label: option.label || option.value,
                disabled: option.disabled || false
              })
            }
          }
        })
        
        return options.length > 0 ? options : [{ value: '', label: 'Seleccione' }]
      }
      // Si no es array, retornar opción por defecto
      return [{ value: '', label: 'Seleccione' }]
    } catch (error) {
      console.error('Error al convertir opciones:', error)
      return [{ value: '', label: 'Seleccione' }]
    }
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
      {/* Dirección de Facturación */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex justify-between items-center mb-4">
          <SectionTitle icon={MapPinIcon} noMargin>Dirección de Facturación</SectionTitle>
          {showRecogeEnBodega && onRecogeEnBodegaChange && (
            <div className="flex flex-wrap items-center gap-2 justify-end">
              <Switch
                checked={recogeEnBodega}
                onChange={onRecogeEnBodegaChange}
                disabled={mode === 'view'}
                label="Recoge en bodega"
              />
              {recogeEnBodega && onBodegaRecogidaSeleccionChange && (
                <div className="w-auto max-w-[9.5rem] sm:max-w-[10.5rem]">
                  <Select
                    value={bodegaRecogidaSeleccion}
                    onChange={(e) =>
                      onBodegaRecogidaSeleccionChange(e.target.value as BodegaRecogidaSeleccion)
                    }
                    disabled={mode === 'view'}
                    readOnly={mode === 'view'}
                    mode={mode}
                    options={[
                      { value: '95', label: 'Bodega Xerox' },
                      { value: 'ecommerce', label: 'Ecommerce' }
                    ]}
                    placeholder="Elige bodega"
                    error={!!bodegaRecogidaError}
                    errorMessage={bodegaRecogidaError}
                    size="sm"
                    className="[&_select]:text-xs [&_select]:py-1 [&_select]:pr-7"
                    aria-label="Elige bodega"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="space-y-3">
          <div className={showAddress2 ? "grid grid-cols-2 gap-4" : ""}>
            {/* Dirección 1 */}
            <FormField
              label="Dirección"
              required
              error={errors.billing_address}
            >
              <Input
                type="text" 
                value={billingAddress.address_1 || ''} 
                onChange={(e) => onBillingAddressChange('address_1', e.target.value)}
                readOnly={isReadOnly}
                error={!!errors.billing_address}
                errorMessage={errors.billing_address}
                mode={mode}
              />
            </FormField>
            
            {/* Dirección 2 - Solo mostrar si showAddress2 es true */}
            {showAddress2 && (
              <FormField
                label="Dirección 2"
              >
                <Input
                type="text" 
                value={billingAddress.address_2 || ''} 
                onChange={(e) => onBillingAddressChange('address_2', e.target.value)}
                readOnly={isReadOnly}
                  mode={mode}
              />
              </FormField>
            )}
            </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Departamento */}
            <FormField
              label="Departamento"
              required
              error={errors.billing_state}
            >
              <Select
                value={billingAddress.state || ''}
                onChange={(e) => onBillingAddressChange('state', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderDepartamentoOptions, 'billing')}
                placeholder="Seleccione"
                error={!!errors.billing_state}
                errorMessage={errors.billing_state}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
            
            {/* Ciudad */}
            <FormField
              label="Ciudad"
              required
              error={errors.billing_city}
            >
              <Select
                value={billingAddress.city || ''}
                onChange={(e) => onBillingAddressChange('city', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderCiudadOptions, 'billing')}
                placeholder="Seleccione"
                error={!!errors.billing_city}
                errorMessage={errors.billing_city}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
          </div>
        </div>
      </div>

      {/* Dirección de Envío */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <SectionTitle icon={TruckIcon}>Dirección de Envío</SectionTitle>
        <div className="space-y-3">
          <div className={showAddress2 ? "grid grid-cols-2 gap-4" : ""}>
            {/* Dirección 1 */}
            <FormField
              label="Dirección"
              required
              error={errors.shipping_address}
            >
              <Input
                type="text" 
                value={shippingAddress.address_1 || ''} 
                onChange={(e) => onShippingAddressChange('address_1', e.target.value)}
                readOnly={isReadOnly}
                error={!!errors.shipping_address}
                errorMessage={errors.shipping_address}
                mode={mode}
              />
            </FormField>
            
            {/* Dirección 2 - Solo mostrar si showAddress2 es true */}
            {showAddress2 && (
              <FormField
                label="Dirección 2"
              >
                <Input
                type="text" 
                value={shippingAddress.address_2 || ''} 
                onChange={(e) => onShippingAddressChange('address_2', e.target.value)}
                readOnly={isReadOnly}
                  mode={mode}
              />
              </FormField>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Departamento */}
            <FormField
              label="Departamento"
              required
              error={errors.shipping_state}
            >
              <Select
                value={shippingAddress.state || ''}
                onChange={(e) => onShippingAddressChange('state', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderDepartamentoOptions, 'shipping')}
                placeholder="Seleccione"
                error={!!errors.shipping_state}
                errorMessage={errors.shipping_state}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
            
            {/* Ciudad */}
            <FormField
              label="Ciudad"
              required
              error={errors.shipping_city}
            >
              <Select
                value={shippingAddress.city || ''}
                onChange={(e) => onShippingAddressChange('city', e.target.value)}
                disabled={isReadOnly}
                options={getSelectOptions(renderCiudadOptions, 'shipping')}
                placeholder="Seleccione"
                error={!!errors.shipping_city}
                errorMessage={errors.shipping_city}
                mode={mode}
                readOnly={isReadOnly}
              />
            </FormField>
          </div>
        </div>
      </div>
    </div>
  )
}
